/**
 * "Voice metrics" card — acoustic voice-quality signals from the acoustic
 * service: speaking rate (wpm + level), pauses, pitch variation, voice
 * stability. Everything shown is composed at display time from the payload's
 * language-neutral data (numbers + categorical levels): the levels become
 * localized chips via `terms.*`, and each row's supplementary gloss is a
 * localized template keyed on the level (for pauses, on the rhythm the acoustic
 * service classifies) via `descriptions.*`. The service's own `description`/`acousticSummary`
 * strings are hardcoded Russian, so they are intentionally NOT rendered.
 *
 * Emotion is NEVER surfaced — the acoustic service has emotion recognition
 * disabled (no `emotion` key, #2607), and the acoustic client
 * would drop it at the wire boundary anyway (EU AI Act Art. 5(1)(f));
 * `voiceMetrics` carries no emotion field at all.
 */

import { useTranslation } from 'react-i18next';
import type { VoiceMetrics } from '@aica/api-types/schemas';
import { formatPauseValue, resolvePauseRhythm } from '@/lib/records/pause-rhythm';
import { AnalysisSection, Tag } from './analysis-utils';

interface VoiceMetricsSectionProps {
  voiceMetrics?: VoiceMetrics;
}

// H-1: a WPM outside this range is a degraded input (bad VAD/syllable read
// feeding the articulation_rate fallback), not a real speaking rate —
// belt-and-suspenders alongside the acoustic service's own server-side gate,
// since older records may still carry a garbage value computed before that
// gate existed.
const PLAUSIBLE_WPM_MIN = 40;
const PLAUSIBLE_WPM_MAX = 260;

function VoiceMetricRow({
  label,
  value,
  tag,
  description,
}: {
  label: string;
  value?: string;
  /** Already-translated categorical level, rendered as a standalone chip. */
  tag?: string;
  description?: string;
}) {
  const detail = description?.trim();
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-fg">{label}</span>
        {value && <span className="font-mono text-micro text-fg-subtle">{value}</span>}
        {tag && <Tag variant="neutral">{tag}</Tag>}
      </div>
      {detail && <p className="mt-1 text-2xs leading-relaxed text-fg-muted">{detail}</p>}
    </div>
  );
}

export function VoiceMetricsSection({ voiceMetrics }: VoiceMetricsSectionProps) {
  const { t } = useTranslation('records');
  if (!voiceMetrics) return null;

  const { speechRate, pauses, pitch, voiceQuality } = voiceMetrics;
  // Narrows speechRate to a non-null, plausible reading (H-1). null when the
  // service omitted it (server-side gate) or when an older record still
  // carries a garbage WPM the client-side gate catches.
  const plausibleSpeechRate =
    speechRate != null &&
    speechRate.wpm >= PLAUSIBLE_WPM_MIN &&
    speechRate.wpm <= PLAUSIBLE_WPM_MAX
      ? speechRate
      : null;
  // Localize the known enum levels; degrade gracefully to the raw value if the
  // service ever emits a level outside the mapped set.
  const term = (value: string) =>
    t(`records.analysisPanel.voiceMetrics.terms.${value}`, { defaultValue: value });
  // Localized supplementary gloss; empty default so an unmapped level renders no
  // description rather than a raw i18n key.
  const desc = (path: string) =>
    t(`records.analysisPanel.voiceMetrics.descriptions.${path}`, { defaultValue: '' });

  // Ladder and detail string live in @/lib/records/pause-rhythm — the print
  // report renders the same row and must not be able to word it differently.
  const pauseKey = resolvePauseRhythm(pauses);
  // Value AND caption from the same module: on a degraded VAD read the service
  // zeroes every pause figure, and printing them as measurements contradicted
  // the caption printed directly beneath them.
  const pauseValue = formatPauseValue(pauses, t);

  // Voice-quality gloss: stability interpretation, with a breathiness clause
  // appended only when the service flags audible breath.
  const voiceQualityDesc = [
    desc(`voiceStability.${voiceQuality.stability}`),
    voiceQuality.breathiness === 'breathy' ? desc('breathiness.breathy') : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <AnalysisSection
      title={t('records.analysisPanel.voiceMetrics.title', 'Voice metrics')}
      summary={
        plausibleSpeechRate && (
          <span className="font-mono text-xs font-semibold text-fg-secondary">
            {plausibleSpeechRate.wpm} {t('records.analysisPanel.voiceMetrics.wpm', 'wpm')}
          </span>
        )
      }
    >
      <div className="flex flex-col gap-3 text-xs">
        {plausibleSpeechRate ? (
          <VoiceMetricRow
            label={t('records.analysisPanel.voiceMetrics.speakingRate', 'Speaking rate')}
            value={`${plausibleSpeechRate.wpm} ${t('records.analysisPanel.voiceMetrics.wpm', 'wpm')}`}
            tag={term(plausibleSpeechRate.level)}
            description={desc(`speechRate.${plausibleSpeechRate.level}`)}
          />
        ) : (
          <VoiceMetricRow
            label={t('records.analysisPanel.voiceMetrics.speakingRate', 'Speaking rate')}
            value={t(
              'records.analysisPanel.voiceMetrics.unavailable',
              'Insufficient data'
            )}
          />
        )}

        <VoiceMetricRow
          label={t('records.analysisPanel.voiceMetrics.pauses', 'Pauses')}
          value={pauseValue}
          description={desc(`pauses.${pauseKey}`)}
        />

        <VoiceMetricRow
          label={t('records.analysisPanel.voiceMetrics.pitch', 'Pitch variation')}
          value={`${pitch.rangeHz} ${t('records.analysisPanel.voiceMetrics.hz', 'Hz')}`}
          tag={term(pitch.variability)}
          description={desc(`pitch.${pitch.variability}`)}
        />

        <VoiceMetricRow
          label={t(
            'records.analysisPanel.voiceMetrics.voiceStability',
            'Voice stability'
          )}
          tag={term(voiceQuality.stability)}
          description={voiceQualityDesc}
        />
      </div>
    </AnalysisSection>
  );
}
